import { zodResolver } from "@hookform/resolvers/zod"
import { Add, Public, Telegram, X } from "@mui/icons-material"
import {
  Alert,
  Avatar,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material"

import Grid2 from "@mui/material/Unstable_Grid2/Grid2"
import { createDataItemSigner, message, result } from "@permaweb/aoconnect"
import { useMutation } from "@tanstack/react-query"
import { useActiveAddress } from "arweave-wallet-kit"
import { enqueueSnackbar, SnackbarProvider } from "notistack"
import { useFieldArray, useForm } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"

import z from "zod"

import type { TokenDetails } from "../../api/tokendrop-api"
import TokenCard from "../../components/TokenCard"
import { UploadLogoForm } from "../../components/UploadLogoForm"
import useHopperPrice from "../../hooks/useHopper"
import useTokenDetails from "../../hooks/useTokenDetails"
import { formatUnits, parseUnits } from "../../utils/math"
import BridgeTokensCTA from "@/components/bridge-tokens-cta"
import ENV from "@/env"
import useTokenBalance from "@/hooks/useTokenBalance"
import {
  DEXI_UPDATE_TOKEN_DISCOUNT_PRICE,
  DEXI_UPDATE_TOKEN_FULL_PRICE,
  PAYMENT_TOKENS,
} from "@/settings"

const ACTIVE_PAYMENT_TOKENS = PAYMENT_TOKENS.filter((token) => !token.disabled)
const REDUCED_USD_PRICE = DEXI_UPDATE_TOKEN_DISCOUNT_PRICE
const FULL_USD_PRICE = DEXI_UPDATE_TOKEN_FULL_PRICE

const defaultPaymentToken = ACTIVE_PAYMENT_TOKENS[0]

const formSchema = z.object({
  telegram: z
    .string()
    .optional()
    .refine(
      (value) => !value || /^https:\/\/(t\.me|telegram\.me)\/[a-zA-Z0-9_+]{1,}$/.test(value),
      {
        message: "Please provide a valid Telegram URL (e.g., https://t.me/username)",
      },
    ),
  twitter: z
    .string()
    .optional()
    .refine(
      (value) => !value || /^https:\/\/(twitter\.com|x\.com)\/[a-zA-Z0-9_]{1,15}$/.test(value),
      {
        message: "Please provide a valid Twitter/X URL (e.g., https://twitter.com/username)",
      },
    ),
  website: z
    .string()
    .optional()
    .refine(
      (value) => !value || /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})(\/[\w.-]*)*\/?$/.test(value),
      {
        message: "Please provide a valid URL",
      },
    ),
  customLinks: z.array(
    z.object({
      key: z.string().min(1, "Label is required"),
      value: z.string().url("Must be a valid URL"),
    }),
  ),
  Description: z.string().min(10).max(1000),
  CoverImage: z.string().or(z.literal("")),
  Logo: z.string().or(z.literal("")),
  PaymentToken: z.object({
    label: z.string(),
    value: z.string(),
    denomination: z.number(),
  }),
})

export default function UpdateTokenPage() {
  const { tokenId } = useParams()
  const userAddress = useActiveAddress()
  const { data: tokenInfo, isLoading } = useTokenDetails(tokenId ?? "")
  const { data: balance } = useTokenBalance(tokenId)
  const theme = useTheme()
  const navigate = useNavigate()

  const socials = Array.isArray(tokenInfo?.SocialLinks)
    ? tokenInfo?.SocialLinks
    : Object.entries(tokenInfo?.SocialLinks ?? {}).map(([key, value]) => ({
        key,
        value,
      }))

  const handleLogoUpload = (txid: string) => {
    reset({ ...getValues(), Logo: txid })
  }

  const handleCoverUpload = (txid: string) => {
    reset({ ...getValues(), CoverImage: txid })
  }

  const {
    register,
    getValues,
    control,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      twitter: socials.find((link) => link.key === "Twitter")?.value ?? "",
      telegram: socials.find((link) => link.key === "Telegram")?.value ?? "",
      website: socials.find((link) => link.key === "Website")?.value ?? "",
      customLinks: socials.filter((link) => !["Twitter", "Telegram", "Website"].includes(link.key)),
      Description: tokenInfo?.Description,
      CoverImage: tokenInfo?.CoverImage,
      Logo: tokenInfo?.Logo,
      PaymentToken: defaultPaymentToken,
    },
  })

  const {
    data: paymentTokenPrice,
    isLoading: isPaymentTokenPriceLoading,
    isRefetching: isPaymentTokenRefeching,
  } = useHopperPrice(getValues("PaymentToken.value"))

  const { fields, append, remove } = useFieldArray({
    control,
    name: "customLinks",
  })

  const { data: paymentTokenBalance } = useTokenBalance(getValues("PaymentToken.value"))

  const testMock = {
    ...watch(),
    RenounceOwnership: tokenInfo?.RenounceOwnership ?? false,
    Name: tokenInfo?.Name ?? "",
    SocialLinks: [
      { key: "Website", value: watch("website") },
      { key: "Telegram", value: watch("telegram") },
      { key: "Twitter", value: watch("twitter") },
      ...fields.map((field) => field),
    ],
  }

  const {
    isPending: isPendingMutation,
    mutateAsync,
    isError,
    error,
  } = useMutation({
    mutationKey: ["update-token", tokenId],
    mutationFn: async (values: z.infer<typeof formSchema>) => {
      if (!tokenId) {
        throw new Error("Token ID not provided")
      }

      const SLIPPAGE = 0.05
      const totalPrice = paymentTokenPrice
        ? REDUCED_USD_PRICE * paymentTokenPrice * (1 + SLIPPAGE)
        : 0

      const updateTokenMsgId = await message({
        process: values.PaymentToken.value,
        tags: [
          {
            name: "Action",
            value: "Transfer",
          },
          {
            name: "Recipient",
            value: ENV.VITE_DEXI_PROCESS,
          },
          {
            name: "Quantity",
            value: parseUnits(totalPrice.toString(), values.PaymentToken.denomination).toString(),
          },
          {
            name: "Sender",
            value: userAddress as string,
          },
          {
            name: "X-Action",
            value: "Update-Token-Profile",
          },
          {
            name: "X-Token-Process",
            value: tokenId,
          },
          {
            name: "X-Details",
            value: JSON.stringify({
              SocialLinks: [
                ...values.customLinks,
                { key: "Website", value: values.website },
                { key: "Telegram", value: values.telegram },
                { key: "Twitter", value: values.twitter },
              ],
              Description: values.Description,
              CoverImage: values.CoverImage,
              Logo: values.Logo,
            }),
          },
        ],
        signer: createDataItemSigner(window.arweaveWallet),
      })

      const messageResult = await result({
        // the arweave TXID of the message
        message: updateTokenMsgId,
        // the arweave TXID of the process
        process: values.PaymentToken.value,
      })

      console.log("Message Result", messageResult)
    },
    onSuccess: () => {
      enqueueSnackbar("Token profile updated successfully", { variant: "success" })
      return navigate(`/token/${tokenId}`)
    },
  })

  if (!tokenId) {
    return <div>Token ID not provided</div>
  }

  if (!userAddress) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        Connect your wallet to update a token
      </div>
    )
  }

  if (isLoading || !tokenInfo) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        Loading...
      </div>
    )
  }

  async function onSubmit(values: z.infer<typeof formSchema>) {
    console.log("Submit", values)

    try {
      await mutateAsync(values)
    } catch (error) {
      console.error("Error updating token profile", error)
    }
  }

  const totalPriceReal = paymentTokenPrice
    ? DEXI_UPDATE_TOKEN_DISCOUNT_PRICE * paymentTokenPrice
    : 0
  const totalPrice = totalPriceReal + totalPriceReal * 0.05

  const hasEnoughPaymentTokenBalance =
    Number(
      formatUnits(BigInt(paymentTokenBalance || "0"), getValues("PaymentToken.denomination")),
    ) >= totalPrice

  const isDeployer = userAddress === tokenInfo.Deployer
  const hasMoreThan50PercentBalance = BigInt(balance || "0") > BigInt(tokenInfo.TotalSupply) / 2n

  return (
    <SnackbarProvider autoHideDuration={3000} maxSnack={3}>
      <Grid2 container spacing={8} sx={{ margin: "auto", maxWidth: 1200 }}>
        <Grid2 xs={12}>
          <Stack direction={"column"} alignItems={"center"}>
            <Typography variant="h4">Enhanced Token Profile</Typography>
            <Typography variant="body1">
              Update your DEXI token page with accurate and up-to-date info and Social Media links.
            </Typography>
          </Stack>
        </Grid2>
        <Grid2 xs={12} sm={6} md={8}>
          {isDeployer || hasMoreThan50PercentBalance ? (
            <>
              <Typography variant="h5" sx={{ marginBottom: 2 }}>
                Update Token Profile
              </Typography>
              <Paper sx={{ padding: 4 }}>
                <Stack direction={"column"} spacing={2}>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <Stack direction={"column"} spacing={3}>
                      <Typography variant="h6">Social Links</Typography>

                      <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                          label={"X"}
                          defaultValue={watch("twitter")}
                          sx={{ flexGrow: 1 }}
                          InputProps={{
                            startAdornment: <X sx={{ marginRight: 2 }} />,
                          }}
                          error={!!errors.twitter?.message}
                          helperText={errors.twitter?.message}
                          {...register("twitter")}
                        />
                      </Stack>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                          label={"Telegram"}
                          defaultValue={watch("telegram")}
                          sx={{ flexGrow: 1 }}
                          InputProps={{
                            startAdornment: <Telegram sx={{ marginRight: 2 }} />,
                          }}
                          error={!!errors.telegram?.message}
                          helperText={errors.telegram?.message}
                          {...register("telegram")}
                        />
                      </Stack>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                          label={"Website"}
                          defaultValue={watch("website")}
                          sx={{ flexGrow: 1 }}
                          InputProps={{
                            startAdornment: <Public sx={{ marginRight: 2 }} />,
                          }}
                          error={!!errors.website?.message}
                          helperText={errors.website?.message}
                          {...register("website")}
                        />
                      </Stack>
                      {fields.map((field, index) => (
                        <Stack key={field.id} direction="row" spacing={2} alignItems="center">
                          <TextField
                            label="Label"
                            defaultValue={field.key}
                            error={!!errors.customLinks?.[index]?.key?.message}
                            helperText={errors.customLinks?.[index]?.key?.message}
                            {...register(`customLinks.${index}.key`)}
                          />
                          <TextField
                            label="Value"
                            defaultValue={field.value}
                            error={!!errors.customLinks?.[index]?.value?.message}
                            helperText={errors.customLinks?.[index]?.value?.message}
                            {...register(`customLinks.${index}.value`)}
                            sx={{ flexGrow: 1 }}
                          />
                          <Button
                            onClick={() => {
                              remove(index)
                            }}
                          >
                            Remove
                          </Button>
                        </Stack>
                      ))}
                      {errors.customLinks && <p className="text-red-500">Invalid custom links</p>}
                      <Button onClick={() => append({ key: "", value: "" })} variant="outlined">
                        <Add /> Add Social Link
                      </Button>

                      <TextField
                        id="description"
                        label="Description"
                        multiline
                        rows={4}
                        error={!!errors.Description?.message}
                        helperText={
                          errors.Description?.message ??
                          "Plain text only. Emojis and multilines allowed. Description will be displayed on the pair details page."
                        }
                        defaultValue={tokenInfo.Description}
                        {...register("Description")}
                      />

                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems={"start"}
                        justifyContent={"space-between"}
                      >
                        <FormControl
                          sx={{
                            border: 1,
                            borderColor: "#555",
                            borderRadius: "0.75rem",
                            position: "relative",
                            padding: 2,
                            width: "100%",
                          }}
                        >
                          <Box
                            sx={{
                              position: "absolute",
                              top: "-0.6rem",
                              left: "1rem",
                              fontSize: "0.75rem",
                            }}
                          >
                            Cover Image
                          </Box>
                          <UploadLogoForm
                            onUploadSuccess={handleCoverUpload}
                            currentLogo={watch("CoverImage")}
                            type="CoverImage"
                          />
                        </FormControl>
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems={"start"}
                        justifyContent={"space-between"}
                      >
                        <FormControl
                          sx={{
                            border: 1,
                            borderColor: "#555",
                            borderRadius: "0.75rem",
                            position: "relative",
                            padding: 2,
                            width: "100%",
                          }}
                        >
                          <Box
                            sx={{
                              position: "absolute",
                              top: "-0.6rem",
                              left: "1rem",
                              fontSize: "0.75rem",
                            }}
                          >
                            Logo
                          </Box>
                          <UploadLogoForm
                            onUploadSuccess={handleLogoUpload}
                            currentLogo={watch("Logo")}
                            type="Logo"
                          />
                        </FormControl>
                      </Stack>

                      <Box
                        sx={{
                          backgroundColor: theme.palette.background.default,
                          borderRadius: 2,
                          padding: 4,
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Typography variant="body1">Update Price:</Typography>
                          <Typography variant="h6" color="text.primary">
                            {`$${REDUCED_USD_PRICE}`}
                          </Typography>
                          <Typography
                            variant="h6"
                            color="text.secondary"
                            sx={{ textDecoration: "line-through" }}
                          >
                            {`$${FULL_USD_PRICE}`}
                          </Typography>
                        </Stack>

                        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 4 }}>
                          Price will be converted to the selected payment token.
                        </Typography>

                        <FormControl fullWidth>
                          <InputLabel id="demo-simple-select-label">Payment Token</InputLabel>
                          <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Payment Token"
                            {...register("PaymentToken")}
                            defaultValue={ACTIVE_PAYMENT_TOKENS[0]}
                          >
                            {PAYMENT_TOKENS.map((token) => (
                              <MenuItem
                                key={token.value}
                                value={token as any}
                                disabled={token.disabled}
                              >
                                <Stack direction="row" gap={1} alignItems="center">
                                  <Avatar
                                    src={`https://arweave.net/${token.logo}`}
                                    alt={token.label}
                                    sx={{
                                      width: 18,
                                      height: 18,
                                      fontSize: 18 * 0.75,
                                    }}
                                  />
                                  {token.label}
                                </Stack>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <BridgeTokensCTA token={getValues("PaymentToken.value")} />

                        {isPaymentTokenPriceLoading || isPaymentTokenRefeching ? (
                          <Typography variant="body2" color="text.secondary" sx={{ marginTop: 2 }}>
                            Loading price...
                          </Typography>
                        ) : (
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            sx={{ marginTop: 2 }}
                          >
                            <Typography variant="body1">
                              Price in {getValues("PaymentToken.label")}:
                            </Typography>
                            <Typography variant="h6" color="text.primary">
                              {`${(REDUCED_USD_PRICE * (paymentTokenPrice ?? 0)).toFixed(4)} ${getValues("PaymentToken.label")}`}
                            </Typography>
                            <Typography
                              variant="h6"
                              color="text.secondary"
                              sx={{ textDecoration: "line-through" }}
                            >
                              {`${(FULL_USD_PRICE * (paymentTokenPrice ?? 0)).toFixed(4)} ${getValues("PaymentToken.label")}`}
                            </Typography>
                          </Stack>
                        )}
                      </Box>

                      {(isDeployer || hasMoreThan50PercentBalance) && (
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          disabled={
                            isPendingMutation ||
                            isPaymentTokenPriceLoading ||
                            isPaymentTokenRefeching ||
                            !hasEnoughPaymentTokenBalance
                          }
                        >
                          {isPendingMutation
                            ? "Updating..."
                            : !hasEnoughPaymentTokenBalance
                              ? "Insufficient balance"
                              : `Update Token Profile for ${totalPrice.toFixed(4)} ${getValues("PaymentToken.label")}`}
                        </Button>
                      )}

                      {isError && error ? (
                        <Typography variant="body2" color="error">
                          {error.message}
                        </Typography>
                      ) : null}
                    </Stack>
                  </form>
                </Stack>
              </Paper>
            </>
          ) : (
            <Stack gap={4}>
              <Alert severity="error">
                You need to be the deployer or have more than 50% of the token supply to update this
                token.
              </Alert>
              <Button
                variant="contained"
                sx={{ width: "100%" }}
                onClick={() => navigate(`/token/${tokenId}`)}
              >
                Go back
              </Button>
            </Stack>
          )}
        </Grid2>
        <Grid2 xs={12} sm={4} md={4}>
          <Typography variant="h5" sx={{ marginBottom: 2 }}>
            Preview
          </Typography>
          <TokenCard token={tokenId} tokenInfoMock={testMock as unknown as TokenDetails} hideCTA />
        </Grid2>
      </Grid2>
    </SnackbarProvider>
  )
}
